import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { useClassnames } from '@lumapps/classnames';
import { contentSelector } from '@lumapps/contents/ducks/selectors';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiSparkles } from '@lumapps/lumx/icons';
import { Button, Emphasis, Theme } from '@lumapps/lumx/react';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useSelector } from '@lumapps/redux/react';
import { STRUCTURED_CONTENT_WREX_EDITOR_CLASSNAME } from '@lumapps/structured-content/constants';
import { useTranslate } from '@lumapps/translations';
import { BLOCK_STRUCTURED_CONTENT_CLASSNAME, WIDGET_CONTRIBUTION_TYPE } from '@lumapps/widget-contribution/constants';
import {
    BLOCK_HTML_CLASSNAME,
    LEGACY_BLOCK_HTML_CLASSNAME,
    LEGACY_EDITION_HTML_CLASSNAME,
    WIDGET_HTML_TYPE,
} from '@lumapps/widget-html/constants';
import {
    BLOCK_INTRO_CLASSNAME,
    EDITION_INTRO_TEXTAREA_CLASSNAME,
    LEGACY_BLOCK_INTRO_CLASSNAME,
    LEGACY_EDITION_INTRO_CLASSNAME,
    WIDGET_INTRO_TYPE,
    CLASSNAME as WIDGET_INTRO_CLASSNAME,
} from '@lumapps/widget-intro/constants';
import type { Layout } from '@lumapps/widget-layout/types';
import { hasOneOfWidgetTypesInContent } from '@lumapps/widget-layout/utils/hasOneOfWidgetTypesInContent';

import { CLASSNAME } from '../../constants';
import { WIDGET_SUMMARY } from '../../keys';

export interface ButtonGenerateProps {
    /** Forwarded class name */
    className?: string;
    /** Callback called on generate button click */
    onGenerate: (prompt: string) => Promise<void>;
    /** Whether a text has already been generated before */
    hasTextBeenGenerated: boolean;
    /** Whether a generation is ongoing */
    isGeneratingSummary: boolean;
    theme?: Theme;
}

/**
 * Button for generate summary with AI
 * @param ButtonGenerateProps
 * @returns ButtonGenerate
 */
export const ButtonGenerate: React.FC<ButtonGenerateProps> = ({
    className,
    onGenerate,
    hasTextBeenGenerated,
    isGeneratingSummary,
    theme = Theme.light,
}) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(CLASSNAME);
    const currentContent = useSelector(contentSelector);
    const widgetIntroClassname = useClassnames(WIDGET_INTRO_CLASSNAME);
    const blockHtmlClassname = useClassnames(BLOCK_HTML_CLASSNAME);

    const { info: infoNotification } = useNotification();

    const areTextualWidgetInContent = React.useMemo(() => {
        const contentTemplate = currentContent.template as Layout;

        if (contentTemplate) {
            return hasOneOfWidgetTypesInContent(
                [WIDGET_HTML_TYPE, WIDGET_INTRO_TYPE, WIDGET_CONTRIBUTION_TYPE],
                contentTemplate,
            );
        }

        return false;
    }, [currentContent.template]);

    const getTextualWidgetsContent = React.useCallback((textualWidgetsElements: Element[]) => {
        let textualContent = '';
        textualWidgetsElements.forEach((element, index) => {
            // Check for widget rich text placeholder presence
            const isDisplayingPlaceholder = !!element.querySelector('[data-slate-placeholder="true"]');

            // Use innerText if available, otherwise use value
            const widgetContent = (
                (element as HTMLElement).innerText ||
                (element as HTMLTextAreaElement).value ||
                ''
            ).trim();

            if (!isDisplayingPlaceholder && widgetContent !== '') {
                textualContent = textualContent.concat(
                    `\n\n #### Piece of content number ${index + 1}: `,
                    widgetContent,
                );
            }
        });
        return textualContent.trim();
    }, []);

    const handleGenerate = () => {
        const elements = document.querySelectorAll(
            `.${blockHtmlClassname.block()}, .${LEGACY_BLOCK_HTML_CLASSNAME}, .${LEGACY_EDITION_HTML_CLASSNAME} .fr-element, .${BLOCK_STRUCTURED_CONTENT_CLASSNAME}, .${STRUCTURED_CONTENT_WREX_EDITOR_CLASSNAME}, .${BLOCK_INTRO_CLASSNAME}, .${widgetIntroClassname.element(
                EDITION_INTRO_TEXTAREA_CLASSNAME,
            )} textarea, .${LEGACY_BLOCK_INTRO_CLASSNAME}, .${LEGACY_EDITION_INTRO_CLASSNAME} .fr-element`,
        );
        const textualWidgetsContent = getTextualWidgetsContent(Array.from(elements));

        if (!areTextualWidgetInContent || isEmpty(textualWidgetsContent) || isEmpty(textualWidgetsContent.trim())) {
            infoNotification({ content: translateKey(WIDGET_SUMMARY.NO_TEXTUAL_WIDGET_INFO) });
        } else {
            onGenerate(textualWidgetsContent);
        }
    };

    return (
        <Button
            className={className}
            leftIcon={mdiSparkles}
            emphasis={Emphasis.low}
            onClick={handleGenerate}
            isDisabled={isGeneratingSummary}
            theme={theme}
            {...get({ element: 'button', action: 'generate-ai-summary' })}
        >
            {hasTextBeenGenerated
                ? translateKey(WIDGET_SUMMARY.REGENERATE_SUMMARY)
                : translateKey(WIDGET_SUMMARY.GENERATE_SUMMARY)}
        </Button>
    );
};
